"use client";
import { iconList } from "@/assets/icons";
import {
  Controller,
  FormProvider,
  SubmitHandler,
  useForm,
} from "react-hook-form";
import { ArrowRight, CalendarDays } from "lucide-react";
import { useMemo, useState } from "react";
import { DateRange } from "react-day-picker";
import { format } from "date-fns";
import {
  datePickerFormatJP,
  DEFAULT_ADULTS,
  DEFAULT_CHECKIN_DATE,
  DEFAULT_CHECKOUT_DATE,
} from "@/constants";
import { useModal } from "@/hooks/useModal/useModal";
import { GroupOption } from "./SearchAvailableRoom";
import { Label } from "@radix-ui/react-label";
import { RoomSelect } from "./RoomSelect";
import { valibotResolver } from "@hookform/resolvers/valibot";
import { IHotelOrCity } from "@/types/availableRoom";
import { ERoomOrderBy, ERoomType } from "@/types/roomsList";
import { TRIPLA_BOOKING_URL } from "@/constants/enviroment";
import { useValidatedDates } from "@/hooks/useValidatedDates";
import {
  cityAndHotelFuzzyFilter,
  formatDateToYYYYMMDD,
  redirectBookingCard,
  removeTrailingSlashPathName,
  SelectOption,
  formatDateForStorage,
} from "@/utils";
import { LOCALE_BASE_MAP } from "@/i18n/routing";
import * as React from "react";

import * as v from "valibot";

import { useIsMobile } from "@/hooks/useIsMobile/useIsMobile";
import useJapanAreasStore from "@/store/hooks/useGlobal";
import useSearchFormStore from "@/store/searchFormStore/searchFormStore";
import { removeSlugHotelDetailPrefix } from "@/utils/queryUtils/queryUtils";
import { usePathname } from "next/navigation";
import { createBookingParams, deepCloneRoom, TRoom } from "./ultils";
import { CustomSelect } from "@/components/atoms/dropdown";
import { Button } from "@/components/atoms/button";
import { Modal } from "@/components/atoms/modal";
import { Icon } from "@/components/atoms/icon";
import { DateRangePicker } from "@/components/atoms/date-range-picker";
import { Input } from "@/components/atoms/input";
import { useRouter } from "next/navigation";
import {
  convertTimeToSeconds,
  generateLinkFromGeneralLink,
  generateTargetFromLink,
  LinkConfig,
} from "@/lib/utils";
import Link from "next/link";
import CtaButton, { CtaButtonProps } from "@/components/atoms/CtaButton";
import { CustomDatePicker } from "@/components/atoms/custom-date-picker";
// import { CLIENT_ENV } from "@/configs/config";

export interface AgesConfigurations {
  agesLabel?: string;
  agePlaceholder?: string;
  singleChildLabel?: string;
  ageValuePlaceholder?: string;
  invalidAgeError?: string;
}

export interface Booking {
  bookingButtonText?: string;
}

export interface RoomsConfigurations {
  adultLabel?: string;
  childrenLabel?: string;
  addRoomButtonText?: string;
  maximumAdults?: string;
  maximumChildAge?: string;
  maximumChildren?: string;
  maximumRooms?: string;
  singleRoomLabel?: string;
  bedLabel?: string;
  withoutBedLabel?: string;
  withBedLabel?: string;
  default_adult_count?: string;
}

export interface Configurations {
  checkinLabel?: string;
  checkoutLabel?: string;
  promoCodeLabel?: string;
  promoCodePlaceholder?: string;
}

export interface CustomSearchAvailableProps {
  className?: string;
  isSearchByHotelAndCity?: boolean;
  listOption: SelectOption[] | GroupOption[];
  trigger?: React.ReactNode;
  configurations?: Configurations;
  roomsConfigurations?: RoomsConfigurations;
  booking?: Booking;
  agesConfigurations?: AgesConfigurations;
  locale: string;
  hotelId?: string;
  promoCode?: string;
  phone?: string;
  isHiddenMobile?: boolean;
  restaurant_booking_link?: LinkConfig;
  rent_car_link?: LinkConfig;
  tour_link?: LinkConfig;
  jr_package_link?: LinkConfig;
  show_widget?: boolean;
  people?: { MaxPeople: number; MinPeople: number };
  timing?: {
    openingTime: string;
    closingTime: string;
    slotIntervalMinutes: number;
  };
  BookingWidgetCta?: CtaButtonProps;
  bookingRestaurants?: {
    documentId: string;
    display_name?: string | null;
    item_name: string;
    name?: string | null;
    booking_link?: LinkConfig | null | any;
  }[];
  showDate?: boolean;
  initialAdultCount?: string;
  design?: "cri" | "old" | "new-header";
}

function extractKidsAndChildren(rooms: SearchAvailableRoom["rooms"]) {
  const kidsAges: { bed: boolean; age: string; count: number }[] = [];
  let totalChildren = 0;

  if (!rooms) {
    return { totalChildren, kidsAges };
  }

  // Count total children
  for (const room of rooms) {
    if (!room) continue;
    totalChildren += room.children?.length || 0;
  }

  // Build kidsAges from ONLY room[0]
  if (rooms[0]?.children) {
    for (const child of rooms[0].children) {
      if (!child) continue;

      const age = child.age?.value || 0;
      const isBed = child.isBed || false;

      kidsAges.push({
        bed: isBed,
        age: age.toString(),
        count: 1,
      });
    }
  }

  return { totalChildren, kidsAges };
}

export const mapToRequestObject = (data: SearchAvailableRoom) => {
  const { checkIn, checkout, rooms, promoCode, hotelOrCity } = data;

  // const code = CLIENT_ENV === 'production' ? hotelOrCity?.bookingId ?? '' : "01946e82-140c-7fe5-ab6e-60de45c787b1";
  const code = hotelOrCity?.bookingId ?? "";

  // Extract values using helper — SAME LOGIC as original
  const { totalChildren, kidsAges } = extractKidsAndChildren(rooms);

  return {
    code,
    checkin: checkIn ? formatDateToYYYYMMDD(checkIn) : undefined,
    checkout: checkout ? formatDateToYYYYMMDD(checkout) : undefined,
    adults: rooms?.[0]?.adults.toString() || "1",
    children: totalChildren,
    kids_ages: kidsAges,
    room_count: rooms?.length,
    coupon_code: promoCode,
  };
};

export const schemaSearchAvailableRoom = v.object({
  hotelOrCity: v.nullish(
    v.object({
      label: v.string(),
      value: v.string(),
      type: v.optional(v.string()),
      city: v.optional(v.string()),
      bookingId: v.optional(v.string()),
      hotelCode: v.optional(v.string()),
      lat: v.optional(v.string()),
      lng: v.optional(v.string()),
      hotelItemName: v.optional(v.string()),
      slug: v.optional(v.string()),
      zoom: v.optional(v.number()),
      useTripla: v.optional(v.boolean()),
      externalUrl: v.optional(v.string()),
    }),
  ),
  checkIn: v.nullable(v.date()),
  checkout: v.nullable(v.date()),
  rooms: v.optional(
    v.array(
      v.optional(
        v.object({
          adults: v.number(),
          children: v.array(
            v.optional(
              v.object({
                age: v.optional(
                  v.object({
                    label: v.string(),
                    value: v.number(),
                  }),
                ),
                isBed: v.optional(v.boolean()),
              }),
            ),
          ),
        }),
      ),
    ),
  ),
  promoCode: v.optional(v.string()),
  brands: v.optional(
    v.array(
      v.object({
        label: v.string(),
        value: v.string(),
      }),
    ),
  ),
  preferences: v.optional(
    v.array(
      v.object({
        label: v.string(),
        value: v.string(),
      }),
    ),
  ),
});

export type SearchAvailableRoom = v.InferOutput<
  typeof schemaSearchAvailableRoom
>;

const TemplateSearchAvailability: React.FC<CustomSearchAvailableProps> = ({
  listOption,
  trigger,
  configurations,
  roomsConfigurations,
  booking,
  agesConfigurations,
  hotelId,
  promoCode,
  locale,
  restaurant_booking_link,
  rent_car_link,
  tour_link,
  jr_package_link,
  show_widget,
  timing,
  people,
  BookingWidgetCta,
  bookingRestaurants,
  showDate,
  initialAdultCount,
  design,
  ...props
}) => {
  const isCriDesign= design === "cri";
  const [showValidationError, setShowValidationError] = useState(false);
  const { AngleRightSmall } = iconList;
  const [activeTab, setActiveTab] = useState("rooms");
  const [selectedPeople, setSelectedPeople] = useState<string | null>(null);
    const [selectedTime, setSelectedTime] = useState<string | null>(null);
  const [selectedDate, setSelectedDate] = useState<Date | null>(null);

  const [selectedRestaurant, setSelectedRestaurant] = useState<string | null>(
    null,
  );
  const [bookingType, setBookingType] = useState<"rooms" | "restaurant">(
    "rooms",
  );
  const router = useRouter();

  const { setFormData, formData } = useSearchFormStore();
  const { japanAreas, loading } = useJapanAreasStore();
  const isMobile = useIsMobile();
  const currentPathName = usePathname();
  const isDestinationPage =
    currentPathName.includes("/japan") || currentPathName.endsWith("-japan");

  const [range, setRange] = useState<DateRange>({
    from: new Date(DEFAULT_CHECKIN_DATE),
    to: new Date(DEFAULT_CHECKOUT_DATE),
  });

  const [inputValue, setInputValue] = useState<string>("");
  const [previousRange, setPreviousRange] = useState<DateRange>({
    from: new Date(DEFAULT_CHECKIN_DATE),
    to: new Date(DEFAULT_CHECKOUT_DATE),
  });

  const [currentSelectedHotel, setCurrentSelectedHotel] =
    useState<IHotelOrCity | null>(() => {
      if (hotelId) {
        for (const item of listOption) {
          if ("options" in item) {
            const found = item.options.find((opt) => {
              return opt.value === hotelId || opt.bookingId === hotelId;
            });
            if (found) return found as IHotelOrCity;
          } else if (item.value === hotelId || item.bookingId === hotelId) {
            return item as IHotelOrCity;
          }
        }
      }
      return formData?.hotelOrCity || null;
    });

  const { checkIn: validatedCheckIn, checkOut: validatedCheckOut } =
    useValidatedDates(formData?.checkIn, formData?.checkout);

  // Helper: Find hotel from path
  const findHotelFromPath = (
    currentPathName: string,
    listOption: SelectOption[] | GroupOption[],
  ): IHotelOrCity | null => {
    const pathArr = removeTrailingSlashPathName(currentPathName).split("/");
    const hotelSlugFromPath = removeSlugHotelDetailPrefix(
      pathArr[(pathArr?.length ?? 0) - 1],
    );

    if (!hotelSlugFromPath) return null;

    for (const item of listOption) {
      if ("options" in item) {
        const found = item.options.find(
          (opt) => opt.slug === hotelSlugFromPath,
        );
        if (found) {
          return found as IHotelOrCity;
        }
      }
    }
    return null;
  };

  // Helper: Determine initial hotel or city
  const determineInitialHotelOrCity = (
    hotelId: string | undefined,
    currentSelectedHotel: IHotelOrCity | null,
    formData: SearchAvailableRoom | null,
    currentPathName: string,
    listOption: SelectOption[] | GroupOption[],
  ): IHotelOrCity | null => {
    if (hotelId && currentSelectedHotel) {
      return currentSelectedHotel;
    }

    if (formData?.hotelOrCity) {
      return formData.hotelOrCity;
    }

    return findHotelFromPath(currentPathName, listOption);
  };

  // Helper: Determine rooms value
  const determineRooms = (formData: SearchAvailableRoom | null) => {
    if (formData?.rooms && formData.rooms.length > 0) {
      return formData.rooms;
    }
    return [{ adults: Number(DEFAULT_ADULTS), children: [] }];
  };

  // Helper: Determine promo code
  const determinePromoCode = (
    formData: SearchAvailableRoom | null,
    promoCode: string | undefined,
  ): string => {
    return formData?.promoCode || promoCode || "";
  };

  const initialFormDataState = React.useMemo(() => {
    const initialHotelOrCity = determineInitialHotelOrCity(
      hotelId,
      currentSelectedHotel,
      formData,
      currentPathName,
      listOption,
    );

    const { checkIn, checkout } = {
      checkIn: validatedCheckIn,
      checkout: validatedCheckOut,
    };
    const rooms = determineRooms(formData);
    const promoCodeValue = determinePromoCode(formData, promoCode);

    return {
      hotelOrCity: initialHotelOrCity,
      checkIn,
      checkout,
      rooms,
      promoCode: promoCodeValue,
      preferences: formData?.preferences || [],
      brands: formData?.brands || [],
    } as SearchAvailableRoom;
  }, [
    determineInitialHotelOrCity,
    hotelId,
    currentSelectedHotel,
    formData,
    currentPathName,
    listOption,
    validatedCheckIn,
    validatedCheckOut,
    promoCode,
  ]);

  const methods = useForm<SearchAvailableRoom>({
    defaultValues: initialFormDataState,
    resolver: valibotResolver(schemaSearchAvailableRoom),
  });
  const pathArr =
    removeTrailingSlashPathName(currentPathName)?.split("/") || [];
  const hotel_slug = pathArr?.[(pathArr?.length ?? 0) - 1];

  const { handleSubmit, control, formState, setValue, watch } = methods;
  const rooms = watch("rooms");
  const default_adult_count = roomsConfigurations?.default_adult_count;
  const { isOpen, toggleModal } = useModal();

  // Only set the range from the store on initial mount
  React.useEffect(() => {
    setRange({
      from: validatedCheckIn,
      to: validatedCheckOut,
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  React.useEffect(() => {
    const pathArr =
      removeTrailingSlashPathName(currentPathName)?.split("/") || [];
    const hotelSlug = pathArr?.[(pathArr?.length ?? 0) - 1];

    if (hotelSlug) {
      let foundHotel: IHotelOrCity | null = null;
      const cleanHotelSlug = removeSlugHotelDetailPrefix(hotelSlug);

      for (const item of listOption) {
        if ("options" in item) {
          const found = item.options.find((opt) => {
            return (
              opt.slug === hotelSlug ||
              opt.slug === cleanHotelSlug ||
              (hotelId && (opt.value === hotelId || opt.bookingId === hotelId))
            );
          });
          if (found) {
            foundHotel = found as IHotelOrCity;
            break;
          }
        }
      }
      if (
        foundHotel &&
        foundHotel.value !== methods.getValues("hotelOrCity")?.value
      ) {
        setValue("hotelOrCity", foundHotel, { shouldValidate: true });
        setCurrentSelectedHotel(foundHotel);
      }
    }
  }, [currentPathName, setValue, methods, hotelId, listOption]);

  const handleRangeSelect = (
    nextRange: DateRange | undefined,
    selectedDay: Date | undefined,
  ) => {
    // If a range is already selected, clicking any date always starts a new selection
    if (range?.from && range?.to && selectedDay) {
      setRange({ from: selectedDay, to: undefined });
      setValue("checkIn", selectedDay);
      setValue("checkout", null);
      return;
    }
    // Only persist to form when both from and to are selected
    if (nextRange?.from && nextRange?.to) {
      setValue("checkIn", nextRange.from);
      setValue("checkout", nextRange.to);
      setRange(nextRange);
    } else {
      setRange(nextRange || { from: undefined, to: undefined });
    }
  };

  // Only set the range from the store on initial mount
  React.useEffect(() => {
    setRange({
      from: validatedCheckIn,
      to: validatedCheckOut,
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  React.useEffect(() => {
    if (isDestinationPage) {
      if (
        formData?.hotelOrCity &&
        formData.hotelOrCity !== currentSelectedHotel
      ) {
        setCurrentSelectedHotel(formData.hotelOrCity);
      }

      if (formData?.hotelOrCity?.label) {
        setInputValue(formData.hotelOrCity.label);
      }
    }
  }, [currentSelectedHotel, formData, isDestinationPage]);

  // Helper: Route booking to appropriate destination
  const routeBooking = (values: SearchAvailableRoom): void => {
    if (values.hotelOrCity?.type === "city") {
      const slug_hotel =
        japanAreas?.find(
          (item) =>
            item?.area_name?.toLowerCase() ===
            values?.hotelOrCity?.label.toLowerCase(),
        )?.slug_hotel || "";
      const cityListRoute = `${globalThis.location.origin}/${locale}/${slug_hotel}`;
      window.open(cityListRoute, isMobile ? "_self" : "_blank");
      toggleModal();
    } else if (values.hotelOrCity?.useTripla === false) {
      window.open(
        values.hotelOrCity?.externalUrl || values.hotelOrCity?.slug,
        isMobile ? "_self" : "_blank",
      );
    } else {
      const queryParams = createBookingParams(values, {
        type: ERoomType.ROOMS,
        is_day_use: false,
        order: ERoomOrderBy.RECOMMENDED,
        is_including_occupied: false,
        locale: LOCALE_BASE_MAP[locale],
        coupon_code: values?.promoCode || "",
      });

      if (TRIPLA_BOOKING_URL && queryParams.code) {
        redirectBookingCard(
          {
            ...queryParams,
            checkin: queryParams?.checkin || DEFAULT_CHECKIN_DATE,
            checkout: queryParams?.checkout || DEFAULT_CHECKOUT_DATE,
            adults: queryParams?.adults || "2",
            room_count: queryParams?.room_count || 1,
            children: queryParams?.children,
            kids_ages: queryParams?.kids_ages,
            locale:LOCALE_BASE_MAP[locale]
          },
          isMobile,
        );
      } else {
        window.open(
          values.hotelOrCity?.externalUrl || values.hotelOrCity?.slug,
          isMobile ? "_self" : "_blank",
        );
      }
    }
  };

  const onSubmit: SubmitHandler<SearchAvailableRoom> = (
    values: SearchAvailableRoom,
  ) => {
    setShowValidationError(false);
    if (!values.hotelOrCity) {
      setShowValidationError(true);
      return;
    }

    if (globalThis.window !== undefined) {
      setFormData({
        ...values,
        checkIn: formatDateForStorage(range.from, DEFAULT_CHECKIN_DATE),
        checkout: formatDateForStorage(range.to, DEFAULT_CHECKOUT_DATE),
      } as any);
    }

    routeBooking(values);
  };

  const items = useMemo(() => {
    const list = [
      { id: "rooms", label: "お部屋", link: "#" },

      (show_widget !== false || restaurant_booking_link) && {
        id: "restaurant",
        label: "レストラン",
        link: restaurant_booking_link
          ? generateLinkFromGeneralLink(restaurant_booking_link, locale) || "#" // ✅ fallback to "#" if empty string
          : "#",
        target: restaurant_booking_link
          ? generateTargetFromLink(restaurant_booking_link)
          : undefined,
      },

      tour_link && {
        id: "airport",
        label: "航空券付きプラン",
        link: generateLinkFromGeneralLink(tour_link, locale),
        target: generateTargetFromLink(tour_link),
      },

      jr_package_link && {
        id: "jr",
        label: "JR券付きプラン",
        link: generateLinkFromGeneralLink(jr_package_link, locale),
        target: generateTargetFromLink(jr_package_link),
      },

      rent_car_link && {
        id: "rental",
        label: "レンタカー付プラン",
        link: generateLinkFromGeneralLink(rent_car_link, locale),
        target: generateTargetFromLink(rent_car_link),
      },
    ].filter((item): item is NonNullable<typeof item> => {
      if (!item) return false;
      // Restaurant tab: keep if show_widget is true even if link is empty
      if (item.id === "restaurant") return (show_widget ?? true) || !!item.link;
      return !!item.link;
    });

    return list;
  }, [
    rent_car_link,
    restaurant_booking_link,
    tour_link,
    jr_package_link,
    locale,
    show_widget,
  ]);

  const handleCloseModal = () => {
    toggleModal();
  };
  const peopleOptions = useMemo(() => {
  if (!people) return [];
  const options = [];
  const min = Number(people.MinPeople); 
  const max = Number(people.MaxPeople); 
  for (let i = min; i <= max; i++) {
    options.push({ label: `${i}名`, value: String(i) });
  }
  return options;
}, [people]);

  const timeOptions = useMemo(() => {
      if (!timing) return [];
      const options = [];
      const [startHour, startMin] = timing.openingTime
        .split(":")
        .map(Number);
      const [endHour, endMin] = timing.closingTime
        .split(":")
        .map(Number);
  
      const startMinutes = startHour * 60 + startMin;
      let endMinutes = endHour * 60 + endMin;
      const isOvernight = endMinutes <= startMinutes;
      if (isOvernight) {
        endMinutes += 24 * 60;
      }
  
      for (
        let minutes = startMinutes;
        minutes <= endMinutes;
        minutes += timing.slotIntervalMinutes
      ) {
        const normalized = minutes % (24 * 60);
  
        const hours = Math.floor(normalized / 60);
        const mins = normalized % 60;
  
        const timeString = `${String(hours).padStart(2, "0")}:${String(mins).padStart(2, "0")}`;
  
        options.push({ label: timeString, value: timeString });
      }
  
      return options;
    }, [timing]);
  const restaurantOptions = useMemo(() => {
      if (!bookingRestaurants || bookingRestaurants.length === 0) {
        return [];
      }
  
      return bookingRestaurants
        .filter(
          (restaurant) =>
            restaurant.name && restaurant.documentId && restaurant.booking_link,
        ) // Only show restaurants with booking_link
        .map((restaurant) => ({
          label: restaurant.name as string,
          value: restaurant.documentId as string,
          itemName: restaurant.item_name || "",
          bookingLink: restaurant.booking_link || null,
        }));
    }, [bookingRestaurants]);
  // Derive link — no extra state, always fresh
  const selectedRestaurantBookingLink = useMemo(() => {
  if (
    !selectedRestaurant ||
    !bookingRestaurants ||
    bookingRestaurants.length === 0
  ) {
    return null;
  }
  const restaurant = bookingRestaurants.find(
    (r) => r.documentId === selectedRestaurant,
  );

  const link = restaurant?.booking_link;

  // TYPE GUARD
  if (!link || link.type !== "external" || !link.metadata?.url) {
    return link;
  }

  //  build params dynamically
  const paramsObj: Record<string, string> = {};

  if (selectedPeople) {
    paramsObj["reservation[num_people]"] = selectedPeople;
  }

  // if (selectedDate) {
  //   paramsObj["reservation[start_date]"] = formatDate(selectedDate);
  // }

  if (selectedTime) {
    paramsObj["reservation[start_time]"] = String(
      convertTimeToSeconds(selectedTime)
    );
  }

  // always include token (optional)
  paramsObj["reservation[user_token]"] = "";

  const params = new URLSearchParams(paramsObj).toString();

  // if no params at all, return base link
  if (!params) return link;

  const separator = link.metadata.url.includes("?") ? "&" : "?";

  return {
    ...link,
    metadata: {
      ...link.metadata,
      url: `${link.metadata.url}${separator}${params}`,
    },
  };
}, [
  selectedRestaurant,
  bookingRestaurants,
  selectedTime,
  selectedPeople,
]);
    return (
    <div {...props}>
      <Modal
        open={isOpen}
        toggleModal={handleCloseModal}
        containerClassName={`rounded-2xl !w-[90vw] !max-w-[600px] max-h-[90vh] overflow-hidden gap-0  `}
        closeClassName={` ${isCriDesign ? "bg-[#223B1D]" : "bg-[var(--fill-base-main)]"}  text-white rounded-full opacity-100 p-1.5`}
        header={
          <div className={`border-b border-b-[var(--stroke-gray-light)] px-4 pt-4 pb-3 text-left text-xl leading-8 font-[var(--font-semibold)] ${isCriDesign?"text-black":"text-[var(--text-gray-stronger)]"} `}>
            空室を検索
          </div>
        }
        footer={<></>}
        trigger={
          trigger || (
            <Button
              variant="secondary"
              size="lg"
              aria-label={booking?.bookingButtonText || "空室を検索"}
            >
              {booking?.bookingButtonText || ""}
            </Button>
          )
        }
      >
        <div className={`h-fit max-h-[80vh] overflow-y-auto bg-white p-4 `}>
          {/* Top row: お部屋 | レストラン */}
          <div className="relative overflow-hidden rounded-md">
            <div className="grid grid-cols-2 divide-y divide-gray-400">
              {items.map((tab, index) => {
                const isActive = activeTab === tab.id;
                const isLeft = index % 2 === 0;
                const isRestaurantTab = tab.id === "restaurant";
                const useWidget = isRestaurantTab && show_widget !== false;
                const isLastOdd =
                  index === items.length - 1 && items.length % 2 !== 0;

                const tabClassName = `flex h-full w-full items-center justify-center px-4 py-5 text-sm font-medium transition ${
                  isActive
                    ? `${isCriDesign ? "bg-[#223B1D]" : "bg-[var(--fill-base-main)] rounded-md "} text-white`
                    : `${isCriDesign ? "hover:bg-[#2f5428] hover:text-white" : "bg-white text-[var(--text-base-primary)] hover:bg-gray-50"} `
                }`;

                return (
                  <div
                    key={tab.id}
                    className={`relative p-2 ${isLastOdd ? "col-span-2" : ""}`}
                  >
                    {/* Vertical divider — skip for last odd item */}
                    {isLeft && !isLastOdd && (
                      <span className="absolute top-1/2 right-0 h-16 -translate-y-1/2 border-l border-gray-400" />
                    )}

                    {useWidget ? (
                      <button
                        type="button"
                        className={tabClassName}
                        onClick={() => {
                          setActiveTab(tab.id);
                          setBookingType("restaurant");
                        }}
                      >
                        {tab.label}
                      </button>
                    ) : (
                      <Link
                        href={tab.link}
                        target={tab.target}
                        className={tabClassName}
                        onClick={() => {
                          if (tab.id === "rooms") {
                            setActiveTab(tab.id);
                            setBookingType("rooms");
                          }
                        }}
                      >
                        {tab.label}
                      </Link>
                    )}
                  </div>
                );
              })}
            </div>

            {items.length > 1 && (
              <span className="absolute bottom-0 left-0 w-full border-t border-gray-400" />
            )}
          </div>
          {bookingType === "rooms" && (
            <FormProvider {...methods}>
              <form onSubmit={handleSubmit(onSubmit)}>
                <div className="mt-5 overflow-y-auto px-4 pt-3 pb-4 font-[var(--font-medium)]">
                  <div className={`flex flex-col gap-4 text-[var(--color-primary-950)]`}>
                    <div>
                      <Label className={`mb-1.5 font-[var(--font-medium)] ${isCriDesign ? "text-[#223B1D]" : "text-[var(--color-primary-950)]"}`}>
                        エリア名／ホテル名
                      </Label>
                      <Controller
                        name="hotelOrCity"
                        control={control}
                        render={({ field }) => (
                          <CustomSelect
                            value={field.value}
                            options={listOption}
                            onChange={(selectedOption) => {
                              if (hotelId) {
                                const currentSelection =
                                  selectedOption as IHotelOrCity | null;
                                field.onChange(currentSelection);
                                setCurrentSelectedHotel(currentSelection);
                              } else {
                                field.onChange(selectedOption);
                              }
                              if (showValidationError) {
                                setShowValidationError(false);
                              }
                              // Priority order for adult count on dropdown change:
                              // 1. formData.rooms exists (user previously changed) → keep their count
                              // 2. selectedOption.initial_adult_count (per-hotel CMS field)
                              // 3. initialAdultCount prop (hotel detail page)
                              // 4. default_adult_count (global CMS config)
                              // 5. DEFAULT_ADULTS (hardcoded fallback)
                              const userHasSavedRooms = !!formData?.rooms && formData.rooms.length > 0;
                              const option = selectedOption as SelectOption | null;
                              if (userHasSavedRooms) {
                                setValue("rooms", formData!.rooms as TRoom[]);
                              } else if (option?.type === "hotel") {
                                const configuredCount =
                                  Number(option?.initial_adult_count) ||
                                  Number(initialAdultCount) ||
                                  Number(default_adult_count) ||
                                  Number(DEFAULT_ADULTS);
                                const currentRooms = deepCloneRoom([...((rooms ?? []) as TRoom[])]);
                                if (currentRooms[0]) {
                                  currentRooms[0] = { ...currentRooms[0], adults: configuredCount };
                                }
                                setValue("rooms", currentRooms);
                              } else if (!userHasSavedRooms && option?.type === "city") {
                                const configuredCount =
                                  Number(default_adult_count) ||
                                  Number(DEFAULT_ADULTS);
                                const currentRooms = deepCloneRoom([...((rooms ?? []) as TRoom[])]);
                                if (currentRooms[0]) {
                                  currentRooms[0] = { ...currentRooms[0], adults: configuredCount };
                                }
                                setValue("rooms", currentRooms);
                              }
                            }}
                            onFocus={() => {
                              if (showValidationError) {
                                setShowValidationError(false);
                              }
                            }}
                            placeholder="エリア名／ホテル名を入力してください"
                            onInputChange={(val) => setInputValue(val)}
                            inputValue={inputValue}
                            showAllButton={inputValue?.length > 0}
                            onShowAll={() => {
                              setInputValue("");
                            }}
                            error={formState.errors.hotelOrCity?.message}
                            className="w-full"
                            size="sm"
                            isLoading={loading}
                            iconCustom={<Icon icon="location-pin" />}
                            filterOption={(option, currentInputValue) => {
                              return cityAndHotelFuzzyFilter(
                                { label: option.label, data: option.data },
                                currentInputValue,
                              );
                            }}
                            styles={{
                              control: (base) => ({
                                ...base,
                                paddingLeft: "12px",
                                paddingRight: "12px",
                                gap: "8px",
                                "&:hover": {
                                  borderColor:
                                    showValidationError && !field.value
                                      ? "var(--fill-red-solid-normal)"
                                      : base.borderColor,
                                },
                                borderColor:
                                  showValidationError && !field.value
                                    ? "var(--fill-red-solid-normal)"
                                    : base.borderColor,
                                borderWidth:
                                  showValidationError && !field.value
                                    ? "2px"
                                    : "1px",
                              }),
                              input: (base) => ({
                                ...base,
                                fontSize: "16px",
                              }),
                              placeholder: (base) => ({
                                ...base,
                                opacity: 0.6,
                                whiteSpace: "nowrap",
                                overflow: "hidden",
                                textOverflow: "ellipsis",
                                fontSize: "12px",
                              }),
                              groupHeading: (base) => ({
                                ...base,
                                fontSize: "14px",
                                fontWeight: "bold",
                                color: "var(--text-gray-stronger)",
                                padding: "8px 12px",
                              }),
                            }}
                          />
                        )}
                      />
                    </div>
                    <div>
                      <Label className={`mb-1.5 font-[var(--font-medium)] ${isCriDesign ? "text-[#223B1D]" : "text-[var(--color-primary-950)]"}`}>
                        {configurations?.checkinLabel || ""} -{" "}
                        {configurations?.checkoutLabel || ""}
                      </Label>
                      <DateRangePicker
                        range={range}
                        onChange={handleRangeSelect}
                        onCancel={() => {
                          setRange(previousRange);
                        }}
                      >
                        <Button
                          hierarchy="outline"
                          variant="neutral"
                          className="flex w-full items-center justify-between rounded-md border border-gray-300 px-3 py-4 text-sm font-medium text-[var(--text-gray-light)]"
                        >
                          <span>
                            {range?.from
                              ? format(range.from, datePickerFormatJP)
                              : configurations?.checkinLabel || ""}
                          </span>
                          <ArrowRight
                            className="text-[var(--text-gray-light)]"
                            width={16}
                            height={16}
                          />
                          <span>
                            {range?.to
                              ? format(range.to, datePickerFormatJP)
                              : configurations?.checkoutLabel || ""}
                          </span>
                          <Icon icon={"calendar"} />
                        </Button>
                      </DateRangePicker>
                    </div>
                    <div>
                      <RoomSelect
                        isCustomTemplate={true}
                        design={design}
                        roomConfiguration={roomsConfigurations}
                        AgesConfigurations={agesConfigurations}
                        initial_adult_count={initialAdultCount}
                      />
                    </div>
                    <div>
                      <Label className={`font-medium ${isCriDesign ? "text-[#223B1D]" : "text-[var(--color-primary-950)]"}`}>
                        {configurations?.promoCodeLabel || ""}
                      </Label>
                      <Controller
                        name="promoCode"
                        control={control}
                        render={({ field }) => (
                          <Input
                            className={`w-full rounded-sm border border-gray-300 placeholder:text-sm ${isCriDesign?"text-[#223B1D]":""}`}
                            value={field.value ? field.value : promoCode || ""}
                            onChange={(e) => {
                              field.onChange(e);
                            }}
                            placeholder={
                              configurations?.promoCodePlaceholder || ""
                            }
                            size="sm"
                          />
                        )}
                      />
                    </div>
                  </div>
                </div>

                <div className={"w-full border-t border-t-[var(--stroke-gray-light)] px-4 pt-3"}>
                  <Button
                    variant="secondary"
                    className={`flex w-full items-center justify-between rounded-full ${design === "cri" ? "bg-[#AA874E] hover:bg-[#AA874E] active:bg-[#AA874E] text-[#ffffff] hover:text-[#ffffff] active:text-[#ffffff]" : ""}  `}
                    size="lg"
                    type="submit"
                    disabled={!formState.isValid && formState.isSubmitted}
                  >
                    <span>{booking?.bookingButtonText || ""}</span>
                    <AngleRightSmall width={20} height={20} />
                  </Button>
                </div>
              </form>
            </FormProvider>
          )}
          {bookingType === "restaurant" && (
            <>
              <div className="mt-5 px-4 pb-4">
                <div className="flex flex-col gap-4">
                  {/* Restaurant Name */}
                  <div>
                    <Label className={`font-[var(--font-medium)] ${isCriDesign ? "text-white" : "text-[var(--color-primary-950)]"}`}>
                      レストラン名
                    </Label>
                    <CustomSelect<SelectOption, false>
                      options={restaurantOptions}
                      onChange={(newValue) => {
                        setSelectedRestaurant(newValue?.value || null);
                      }}
                      placeholder="レストラン名を選択してください"
                      className="w-full"
                      menuPlacement={timing && people ? "bottom" : "top"}
                      maxMenuHeight={95}
                      size="sm"
                      iconCustom={<Icon icon="location-pin" />}
                      styles={{
                        control: (base) => ({
                          ...base,
                          paddingLeft: "12px",
                          paddingRight: "12px",
                          gap: "8px",
                          fontSize: "14px",
                        }),
                        placeholder: (base) => ({
                          ...base,
                          opacity: 1,
                          fontSize: "14px",
                          color: "#A4A7AE",
                        }),
                        singleValue: (base) => ({
                          ...base,
                          fontSize: "14px",
                          color: "#102A56",
                        }),
                        option: (base) => ({
                          ...base,
                          fontSize: "14px",
                          color: "#102A56",
                        }),
                      }}
                    />
                  </div>

                  {/* Date */}
                  {showDate && (
                    <div>
                      <Label className={`font-[var(--font-medium)] ${isCriDesign ? "text-white" : "text-[var(--color-primary-950)]"}`}>
                        日付
                      </Label>
                      <CustomDatePicker
                      value={selectedDate}
                      onChange={setSelectedDate}
                      placeholder="日付"
                      minDate={new Date()}
                      className="h-9 w-full rounded-md text-sm font-medium text-[var(--text-gray-light)] placeholder:text-[#A4A7AE] focus:border-none active:border-none"
                      showRightIcon={false}
                      leftIcon={
                        <CalendarDays className="h-5 w-6 text-gray-400" />
                      }
                    />
                  </div>)}
                  {/* Time */}
                  {timing && (
                    <div>
                      <Label className={`font-[var(--font-medium)] ${isCriDesign ? "text-white" : "text-[var(--color-primary-950)]"}`}>
                        時間
                      </Label>
                      <CustomSelect<SelectOption, false>
                        options={timeOptions}
                        onChange={(val) => setSelectedTime(val?.value || null)}
                        placeholder="時間を選択"
                        className="w-full"
                        size="sm"
                        menuPlacement="top"
                        maxMenuHeight={160}
                        styles={{
                          control: (base) => ({
                            ...base,
                            paddingLeft: "12px",
                            paddingRight: "12px",
                            gap: "8px",
                            fontSize: "13px",
                          }),
                          input: (base) => ({
                            ...base,
                            fontSize: "16px",
                          }),
                          placeholder: (base) => ({
                            ...base,
                            opacity: 1,
                            whiteSpace: "nowrap",
                            overflow: "hidden",
                            textOverflow: "ellipsis",
                            fontSize: "12px",
                            color: "#A4A7AE",
                          }),
                          singleValue: (base) => ({
                            ...base,
                            fontSize: "13px",
                            color: "#102A56",
                          }),
                          option: (base) => ({
                            ...base,
                            fontSize: "13px",
                            color: "#102A56",
                          }),
                        }}
                      />
                    </div>
                  )}

                  {/* People */}
                  {people && (
                    <div>
                      <Label className={`font-[var(--font-medium)] ${isCriDesign ? "text-white!" : "text-[var(--color-primary-950)]"}`}>
                        人数
                      </Label>
                      <CustomSelect<SelectOption, false>
                        options={peopleOptions}
                        onChange={(val) => setSelectedPeople(val?.value || null)}
                        placeholder="人数を選択"
                        className="w-full"
                        size="sm"
                        menuPlacement="top"
                        maxMenuHeight={160}
                        styles={{
                          control: (base) => ({
                            ...base,
                            paddingLeft: "12px",
                            paddingRight: "12px",
                            gap: "8px",
                            fontSize: "13px",
                          }),
                          input: (base) => ({
                            ...base,
                            fontSize: "16px",
                          }),
                          placeholder: (base) => ({
                            ...base,
                            opacity: 1,
                            whiteSpace: "nowrap",
                            overflow: "hidden",
                            textOverflow: "ellipsis",
                            fontSize: "14px",
                            color: "#A4A7AE",
                          }),
                          singleValue: (base) => ({
                            ...base,
                            fontSize: "13px",
                            color: "#102A56",
                          }),
                          option: (base) => ({
                            ...base,
                            fontSize: "13px",
                            color: "#102A56",
                          }),
                        }}
                      />
                    </div>
                  )}
                </div>
              </div>

              <div className="w-full border-t border-t-[var(--stroke-gray-light)] px-4 pt-3">
                
                  <div className="w-full">
                    {selectedRestaurantBookingLink ? (
                      <CtaButton
                        {...(BookingWidgetCta || { label: "予約する" })}
                        hyperlink={selectedRestaurantBookingLink}
                        className={`flex h-9 w-full min-w-full justify-between rounded-4xl  px-5 text-sm whitespace-nowrap text-[var(--color-primary-950)] hover:!border-2 hover:!border-solid ${design === "cri" ? "bg-[#C9A961] text-[#2D4A36]" : "hover:!border-[#FFB100] hover:!text-[#FFB100] border-[#FFB100] hover:![background:transparent]"}  `}
                        icon={<AngleRightSmall width={20} height={20} />}
                      />
                    ) : (
                      <CtaButton
                       {...(BookingWidgetCta || { label: "予約する" })}
                        className={`flex h-9 w-full min-w-full justify-between rounded-4xl  px-5 text-sm whitespace-nowrap text-[var(--color-primary-950)] hover:!border-2 hover:!border-solid ${design === "cri" ? "bg-[#C9A961] text-[#2D4A36]" : "hover:!border-[#FFB100] hover:!text-[#FFB100] border-[#FFB100] hover:![background:transparent]"} `}
                        icon={<AngleRightSmall width={20} height={20} />}
                      />
                    )}
                  </div>
                
              </div>
            </>
          )}
        </div>
      </Modal>
    </div>
  );
};

export default TemplateSearchAvailability;
